import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ProductLogo } from '@qwickapps/react-framework';
import { MustacheTemplateProvider } from '@qwickapps/schema';
import { useDocument } from '../contexts/DocumentContext';
import { createDefaultIcon } from '../data/defaultLetterhead';

export const LetterheadFooter: React.FC = () => {
  const { letterheadConfig } = useDocument();

  const templateProvider = useMemo(() => new MustacheTemplateProvider(), []);

  if (!letterheadConfig.enabled) {
    return null;
  }

  // If custom template is provided, use it
  if (letterheadConfig.footerTemplate) {
    const hasLogoPlaceholder = letterheadConfig.footerTemplate.includes('{{logo}}');

    const templateContext = {
      companyName: letterheadConfig.companyName,
      tagline: letterheadConfig.tagline,
      address: letterheadConfig.address,
      email: letterheadConfig.email,
      phone: letterheadConfig.phone,
      website: letterheadConfig.website,
      date: new Date().toLocaleDateString(),
      logo: hasLogoPlaceholder ? '<span data-logo-placeholder="true"></span>' : '',
    };

    let renderedHtml = templateProvider.resolve(letterheadConfig.footerTemplate, templateContext);

    return (
      <Box
        sx={{
          p: 1,
          m: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        {hasLogoPlaceholder ? (
          <>
            {renderedHtml.split('<span data-logo-placeholder="true"></span>').map((part, index, arr) => (
              <React.Fragment key={index}>
                {part && <Box dangerouslySetInnerHTML={{ __html: part }} />}
                {index < arr.length - 1 && (
                  <ProductLogo
                    name={letterheadConfig.logoText || letterheadConfig.companyName}
                    size={letterheadConfig.logoSize}
                    variant={letterheadConfig.logoVariant}
                    icon={
                      letterheadConfig.logoUrl
                        ? React.createElement('img', {
                            src: letterheadConfig.logoUrl,
                            alt: letterheadConfig.companyName,
                            style: {
                              display: 'block',
                              height: '32px',
                              width: '32px',
                            },
                          })
                        : createDefaultIcon()
                    }
                  />
                )}
              </React.Fragment>
            ))}
          </>
        ) : (
          <Box dangerouslySetInnerHTML={{ __html: renderedHtml }} sx={{ width: '100%' }} />
        )}
      </Box>
    );
  }

  // Default footer layout: address • phone • email • website
  return (
    <Box
      sx={{
        p: 1,
        m: 0,
        borderTop: '1px solid',
        borderColor: 'divider',
        textAlign: 'center',
        backgroundColor: 'background.paper',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {letterheadConfig.address != '' && `${letterheadConfig.address} • `}
        {letterheadConfig.phone != '' && `${letterheadConfig.phone} • `}
        {letterheadConfig.email != '' && `${letterheadConfig.email} • `}
        {letterheadConfig.website}
      </Typography>
    </Box>
  );
};
